<script setup>
import { TypeChart } from "@/utilities/enum";
import { formatFloat } from "@/utilities/helpers";
</script>

<template>
  <div class="bg-white rounded-md shadow-md">
    <div class="flex p-3 items-center justify-between border-b-2 border-dashed">
      <div class="flex items-center justify-start gap-2">
        <div
          class="min-w-7 w-7 h-7 rounded-md flex justify-center items-center"
          :class="bgColorIcon"
        >
          <img
            :src="require(`../../../assets/icon/${icon}`)"
            alt="icon-fuel"
            class="w-4 h-auto"
            loading="eager"
          />
        </div>
        <h5 class="text-xs font-semibold text-gray-800">
          {{ title }}
        </h5>
      </div>
      <div class="flex items-center justify-end gap-3">
        <div class="rounded-md bg-slate-50 p-1 flex gap-2" v-if="!compare">
          <div
            class="px-2 py-1 cursor-pointer"
            v-for="data in chartViews"
            :key="data.id"
            :class="{
              'bg-white shadow-md rounded': tabActive === data.id,
            }"
            @click="changeView(data.id)"
          >
            <h5 class="text-[10px] font-semibold text-slate-800">
              {{ data.title }}
            </h5>
          </div>
        </div>
        <div
          class="w-5 h-5 rounded-md bg-gray-500 flex justify-center items-center cursor-pointer"
          @click="$router.push(`/dashboard-esg/${type}/${area}`)"
          v-if="!isLarge"
        >
          <img
            src="../../../assets/icon/arrow.svg"
            alt="arrow"
            class="rotate-180 w-4 h-auto"
          />
        </div>
        <v-btn
          density="compact"
          variant="outlined"
          class="capitalize font-medium text-[10px] border-[2px] text-slate-600 border-slate-300"
          v-if="isLarge && type === TypeChart.FUEL && isAdmin"
          @click="getCompare"
        >
          <img
            src="./../../../assets/icon/fatrows.png"
            alt="Compare Icon"
            class="mr-2 h-3 w-auto"
          />
          Lihat perbandingan
          <img
            src="./../../../assets/icon/close.png"
            alt="Close Icon"
            class="ml-2 h-3 w-auto"
            v-if="compare"
          />
        </v-btn>
      </div>
    </div>
    <v-card elevation="0" :loading="loading" class="px-3 pb-3">
      <template v-if="compare">
        <div class="flex justify-between items-center pt-3 px-2">
          <v-autocomplete
            variant="outlined"
            hide-details
            color="#475569"
            density="compact"
            placeholder="Semua Mitra"
            class="max-w-56"
            :items="initialData"
            item-title="title"
            item-value="value"
            v-model="mitra"
            multiple
            chips
            closable-chips
            @update:model-value="getCompareChart(mitra)"
          >
          </v-autocomplete>
          <div class="flex items-center gap-4">
            <v-checkbox
              v-model="displayCompare.fuel"
              hide-details
              density="compact"
              color="#dc2626"
            >
              <template v-slot:label>
                <h6 class="text-xs font-medium ml-1">Fuel</h6>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="displayCompare.distance"
              hide-details
              density="compact"
              color="#dc2626"
            >
              <template v-slot:label>
                <h6 class="text-xs font-medium ml-1">Distance</h6>
              </template>
            </v-checkbox>
            <v-checkbox
              v-model="displayCompare.ob_coal"
              hide-details
              density="compact"
              color="#dc2626"
            >
              <template v-slot:label>
                <h6 class="text-xs font-medium ml-1">OB/Coal</h6>
              </template>
            </v-checkbox>
          </div>
        </div>
        <div
          class="w-full my-4 overflow-x-auto overflow-y-hidden relative px-2"
          :style="{ height: height + 50 + 'px' }"
        >
          <div class="flex items-end gap-5 min-w-max relative h-full">
            <div
              class="w-32 flex justify-between items-end relative mb-4"
              v-for="(data, index) in compareData"
              :key="index"
              ref="blocks"
            >
              <div
                class="w-9 bg-red-600 rounded-t relative flex justify-center"
                :style="{ height: calculateHeightCompare(data.fuel) + 'px' }"
                :class="[displayCompare.fuel ? 'opacity-100' : 'opacity-0']"
              >
                <div
                  class="absolute text-xs text-slate-800 font-semibold -top-4"
                >
                  {{ data.fuel }}
                </div>
              </div>
              <div
                class="w-9 bg-red-900 rounded-t relative flex justify-center"
                :style="{
                  height: calculateHeightCompare(data.distance) + 'px',
                }"
                :class="[displayCompare.distance ? 'opacity-100' : 'opacity-0']"
              >
                <div
                  class="absolute text-xs text-slate-800 font-semibold -top-4"
                >
                  {{ data.distance }}
                </div>
              </div>
              <div
                class="w-9 bg-gray-400 rounded-t relative flex justify-center"
                :style="{ height: calculateHeightCompare(data.ob_coal) + 'px' }"
                :class="[displayCompare.ob_coal ? 'opacity-100' : 'opacity-0']"
              >
                <div
                  class="absolute text-xs text-slate-800 font-semibold -top-4"
                >
                  {{ data.ob_coal }}
                </div>
              </div>

              <div class="absolute -bottom-4 w-full">
                <h4
                  class="text-slate-700 text-center text-[10px] font-semibold"
                >
                  {{ data.initials }}
                </h4>
              </div>
            </div>

            <div
              class="absolute bottom-4 left-0 right-0 border-t border-gray-300 h-px"
            ></div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="flex items-center gap-2 mt-3 ml-1">
          <input
            class="form-check-input border-2 border-slate-400"
            type="checkbox"
            v-model="legend.find((data) => data.id === idRatioLegend).show"
          />
          <h6 class="text-xs font-medium mt-1">
            Show {{ legend.find((data) => data.id === idRatioLegend).title }}
          </h6>
        </div>
        <div class="w-full my-4 flex items-end border-b relative">
          <svg
            class="absolute w-full h-full pointer-events-none"
            style="z-index: 13"
            v-if="legend.find((data) => data.id === idRatioLegend).show"
          >
            <polyline
              :points="polylinePoints"
              fill="none"
              stroke="#1e3a8a"
              stroke-width="3"
              stroke-linecap="round"
              stroke-linejoin="round"
              ref="polyline"
            />
          </svg>

          <div
            :class="{
              'px-4 w-1/5': tabActive === 1,
              'px-[2px] w-1/12': tabActive === 2,
            }"
            v-for="(item, index) in data"
            :key="index"
            ref="blocks"
          >
            <div class="w-full relative" :style="{ height: height + 'px' }">
              <v-tooltip :text="formatFloat(item.production)" location="top">
                <template v-slot:activator="{ props }">
                  <div
                    v-bind="props"
                    class="w-4/5 max-w-16 bg-slate-300 absolute left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out flex justify-center items-center rounded-t-md"
                    :style="{
                      height: item.production_px + 'px',
                      bottom:
                        type === TypeChart.FUEL
                          ? item.val_amber_px + item.val_red_px + 'px'
                          : type === TypeChart.ENERGY
                          ? item.val_amber_px +
                            item.val_red_px +
                            item.val_orange_px +
                            item.val_green_px +
                            item.val_emerald_px +
                            'px'
                          : type === TypeChart.EMISSION_TON
                          ? item.val_amber_px +
                            item.val_red_px +
                            item.val_orange_px +
                            item.val_green_px +
                            item.val_lime_px +
                            item.val_emerald_px +
                            'px'
                          : 0,
                    }"
                  >
                    <span
                      class="text-black font-medium text-[8px] bg-slate-300 rounded-xl z-10"
                      :class="{
                        hidden: item.production === 0,
                        'px-1 py-[1px]': tabActive === 1,
                        'px-[2px] py-[2px]': tabActive === 2,
                      }"
                    >
                      {{ formatFloat(item.production) }}
                    </span>
                  </div>
                </template>
              </v-tooltip>
              <div
                class="w-4/5 max-w-16 bg-slate-300 absolute left-1/2 bottom-0 -translate-x-1/2 flex justify-center items-center"
                :style="{
                  height:
                    type === TypeChart.FUEL
                      ? item.val_amber_px + item.val_red_px + 'px'
                      : type === TypeChart.ENERGY
                      ? item.val_amber_px +
                        item.val_red_px +
                        item.val_orange_px +
                        item.val_green_px +
                        item.val_emerald_px +
                        'px'
                      : type === TypeChart.EMISSION_TON
                      ? item.val_amber_px +
                        item.val_red_px +
                        item.val_orange_px +
                        item.val_green_px +
                        item.val_lime_px +
                        item.val_emerald_px +
                        'px'
                      : 0,
                }"
              ></div>
              <v-tooltip :text="formatFloat(item.val_amber)" location="top">
                <template v-slot:activator="{ props }">
                  <div
                    v-bind="props"
                    class="w-4/5 max-w-14 bg-amber-500 absolute left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out flex justify-center items-center"
                    :style="{
                      height: item.val_amber_px + 'px',
                      bottom:
                        type === TypeChart.FUEL
                          ? item.val_red_px + 'px'
                          : type === TypeChart.ENERGY
                          ? item.val_red_px +
                            item.val_orange_px +
                            item.val_green_px +
                            item.val_emerald_px +
                            'px'
                          : type === TypeChart.EMISSION_TON
                          ? item.val_red_px +
                            item.val_orange_px +
                            item.val_green_px +
                            item.val_lime_px +
                            item.val_emerald_px +
                            'px'
                          : 0,
                    }"
                  >
                    <span
                      class="text-black font-medium text-[8px] bg-amber-500 rounded-xl z-10"
                      :class="{
                        hidden: item.val_amber === 0,
                        'px-1 py-[1px]': tabActive === 1,
                        'px-[2px] py-[2px]': tabActive === 2,
                      }"
                    >
                      {{ formatFloat(item.val_amber) }}
                    </span>
                  </div>
                </template>
              </v-tooltip>
              <div
                class="w-4/5 max-w-14 absolute left-1/2 bottom-0 -translate-x-1/2 flex justify-center items-center"
                :class="[
                  ((item.val_amber ?? 0) === 0 && (item.val_red ?? 0) === 0) ||
                  (type !== TypeChart.ENERGY && type !== TypeChart.EMISSION_TON)
                    ? 'hidden'
                    : (item.val_amber ?? 0) < (item.val_red ?? 0) &&
                      (item.val_amber ?? 0) !== 0
                    ? 'bg-amber-500'
                    : (item.val_amber ?? 0) >= (item.val_red ?? 0) &&
                      (item.val_red ?? 0) !== 0
                    ? 'bg-red-600'
                    : (item.val_amber ?? 0) > 0
                    ? 'bg-amber-500'
                    : 'bg-red-600',
                ]"
                :style="{
                  height:
                    type === TypeChart.ENERGY
                      ? item.val_red_px +
                        item.val_orange_px +
                        item.val_green_px +
                        item.val_emerald_px +
                        'px'
                      : type === TypeChart.EMISSION_TON
                      ? item.val_red_px +
                        item.val_orange_px +
                        item.val_green_px +
                        item.val_lime_px +
                        item.val_emerald_px +
                        'px'
                      : 0,
                }"
              ></div>
              <v-tooltip :text="formatFloat(item.val_red)" location="top">
                <template v-slot:activator="{ props }">
                  <div
                    v-bind="props"
                    class="w-4/5 max-w-14 bg-red-600 absolute left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out flex justify-center items-center"
                    :style="{
                      height: item.val_red_px + 'px',
                      bottom:
                        type === TypeChart.FUEL
                          ? 0
                          : type === TypeChart.ENERGY
                          ? item.val_orange_px +
                            item.val_green_px +
                            item.val_emerald_px +
                            'px'
                          : type === TypeChart.EMISSION_TON
                          ? item.val_orange_px +
                            item.val_green_px +
                            item.val_lime_px +
                            item.val_emerald_px +
                            'px'
                          : 0,
                    }"
                  >
                    <span
                      class="text-black font-medium text-[8px] bg-red-600 rounded-xl z-10"
                      :class="{
                        hidden: item.val_red === 0,
                        'px-1 py-[1px]': tabActive === 1,
                        'px-[2px] py-[2px]': tabActive === 2,
                      }"
                    >
                      {{ formatFloat(item.val_red) }}
                    </span>
                  </div>
                </template>
              </v-tooltip>
              <v-tooltip :text="formatFloat(item.val_green)" location="top">
                <template v-slot:activator="{ props }">
                  <div
                    v-bind="props"
                    class="w-2/3 max-w-12 bg-green-400 absolute left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out flex justify-center items-center"
                    :style="{
                      height: item.val_green_px + 'px',
                      bottom:
                        type === TypeChart.ENERGY
                          ? item.val_emerald_px + item.val_orange_px + 'px'
                          : type === TypeChart.EMISSION_TON
                          ? item.val_emerald_px +
                            item.val_lime_px +
                            item.val_orange_px +
                            'px'
                          : 0,
                    }"
                    :class="{
                      hidden:
                        type !== TypeChart.ENERGY &&
                        type !== TypeChart.EMISSION_TON,
                    }"
                  >
                    <span
                      class="text-black font-medium text-[8px] bg-green-400 rounded-xl z-10"
                      :class="{
                        hidden: item.val_green === 0,
                        'px-1 py-[1px]': tabActive === 1,
                        'px-[2px] py-[2px]': tabActive === 2,
                      }"
                    >
                      {{ formatFloat(item.val_green) }}
                    </span>
                  </div>
                </template>
              </v-tooltip>

              <v-tooltip :text="formatFloat(item.val_emerald)" location="top">
                <template v-slot:activator="{ props }">
                  <div
                    v-bind="props"
                    class="w-2/3 max-w-12 bg-emerald-600 absolute left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out flex justify-center items-center"
                    :style="{
                      height: item.val_emerald_px + 'px',
                      bottom:
                        type === TypeChart.ENERGY
                          ? item.val_orange_px + 'px'
                          : type === TypeChart.EMISSION_TON
                          ? item.val_orange_px + item.val_lime_px + 'px'
                          : 0,
                    }"
                    :class="{
                      hidden:
                        type !== TypeChart.ENERGY &&
                        type !== TypeChart.EMISSION_TON,
                    }"
                  >
                    <span
                      class="text-black font-medium text-[8px] bg-emerald-600 rounded-xl z-10"
                      :class="{
                        hidden: item.val_emerald === 0,
                        'px-1 py-[1px]': tabActive === 1,
                        'px-[2px] py-[2px]': tabActive === 2,
                      }"
                    >
                      {{ formatFloat(item.val_emerald) }}
                    </span>
                  </div>
                </template>
              </v-tooltip>

              <v-tooltip :text="formatFloat(item.val_lime)" location="top">
                <template v-slot:activator="{ props }">
                  <div
                    v-bind="props"
                    class="w-2/3 max-w-12 bg-lime-500 absolute left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out flex justify-center items-center"
                    :style="{
                      height: item.val_lime_px + 'px',
                      bottom:
                        type === TypeChart.EMISSION_TON
                          ? item.val_orange_px + 'px'
                          : 0,
                    }"
                    :class="{ hidden: type !== TypeChart.EMISSION_TON }"
                  >
                    <span
                      class="text-black font-medium text-[8px] bg-lime-500 rounded-xl z-10"
                      :class="{
                        hidden: item.val_lime === 0,
                        'px-1 py-[1px]': tabActive === 1,
                        'px-[2px] py-[2px]': tabActive === 2,
                      }"
                    >
                      {{ formatFloat(item.val_lime) }}
                    </span>
                  </div>
                </template>
              </v-tooltip>

              <v-tooltip :text="formatFloat(item.val_orange)" location="top">
                <template v-slot:activator="{ props }">
                  <div
                    v-bind="props"
                    class="w-2/3 max-w-12 bg-orange-200 bottom-0 absolute left-1/2 -translate-x-1/2 transition-all duration-300 ease-in-out flex justify-center items-center"
                    :style="{
                      height: item.val_orange_px + 'px',
                    }"
                    :class="{
                      hidden:
                        type !== TypeChart.ENERGY &&
                        type !== TypeChart.EMISSION_TON,
                    }"
                  >
                    <span
                      class="text-black font-medium text-[8px] bg-orange-200 rounded-xl z-10"
                      :class="{
                        hidden: item.val_orange === 0,
                        'px-1 py-[1px]': tabActive === 1,
                        'px-[2px] py-[2px]': tabActive === 2,
                      }"
                    >
                      {{ formatFloat(item.val_orange) }}
                    </span>
                  </div>
                </template>
              </v-tooltip>

              <v-tooltip :text="formatFloat(item.dot_blue)" location="top">
                <template v-slot:activator="{ props }">
                  <div
                    v-bind="props"
                    class="absolute rounded-lg bg-blue-900 left-1/2 -translate-x-1/2 z-20"
                    :class="{
                      'px-1 py-[1px]': tabActive === 1,
                      'px-[2px] py-[2px]': tabActive === 2,
                    }"
                    :style="{
                      bottom: (item.dot_blue_px ?? 0) - 5 + 'px',
                    }"
                    ref="dots"
                    v-if="legend.find((data) => data.id === idRatioLegend).show"
                  >
                    <h4 class="text-white font-medium text-[8px]">
                      {{ formatFloat(item.dot_blue) }}
                    </h4>
                  </div>
                </template>
              </v-tooltip>

              <div class="absolute -bottom-4 w-full">
                <h4
                  class="text-center text-xs text-gray-600 font-medium text-nowrap"
                >
                  {{ item.title }}
                </h4>
              </div>
            </div>
          </div>
        </div>
        <div
          class="px-3 grid gap-2 pt-2"
          :style="`grid-template-columns: repeat(auto-fit, minmax(80px, 1fr))`"
        >
          <div
            class="flex gap-1 items-start"
            v-for="item in legend"
            :key="item.id"
          >
            <div
              v-if="item.id !== 0"
              class="min-w-2 w-2 h-2 mt-[0.7px] border"
              :class="{
                'bg-slate-200': item.id === 1,
                'bg-amber-500': item.id === 2,
                'bg-red-600': item.id === 3,
                'bg-orange-200': item.id === 4,
                'bg-green-400': item.id === 5,
                'bg-emerald-600': item.id === 6,
                'bg-blue-900': item.id === 7,
                'bg-lime-500': item.id === 8,
              }"
            ></div>
            <p
              class="text-[9px] text-gray-500 font-normal leading-none"
              :class="{
                'font-semibold text-slate-800 text-[10px]': item.id === 0,
              }"
            >
              {{ item.title }}
            </p>
          </div>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script>
export default {
  name: "CardChartV2Component",
  data() {
    return {
      polylinePoints: "",
      chartViews: [
        {
          id: 1,
          title: "Quarterly",
        },
        {
          id: 2,
          title: "Monthly",
        },
      ],
      tabActive: 1,
      compare: false,
      mitra: [0],
      displayCompare: {
        fuel: true,
        distance: true,
        ob_coal: true,
      },
    };
  },
  props: {
    height: Number,
    idRatioLegend: Number,
    title: String,
    icon: String,
    bgColorIcon: String,
    isLarge: Boolean,
    type: String,
    area: String,
    legend: Array,
    data: Array,
    compareData: Array,
    initialData: Array,
    isAdmin: Boolean,
    loading: Boolean,
    getChart: Function,
    getCompareChart: Function,
  },
  watch: {
    data: {
      handler(newData) {
        if (newData) {
          this.updatePolyline();
        }
      },
      deep: true,
      immediate: true,
    },
  },
  mounted() {
    this.updatePolyline();
    window.addEventListener("resize", this.updatePolyline);
  },
  unmounted() {
    window.removeEventListener("resize", this.updatePolyline);
  },
  methods: {
    getCompare() {
      this.compare = !this.compare;
      if (this.compare && this.type === "FUEL") {
        this.getCompareChart(this.mitra);
      } else {
        this.changeView(this.tabActive);
      }
    },
    changeView(id) {
      this.tabActive = id;
      const quarterly = this.tabActive === 1;
      this.getChart(this.type, quarterly);
    },
    calculateHeightCompare(item) {
      const maxValue = this.compareData.reduce((max, d) => {
        return Math.max(max, d.fuel, d.distance, d.ob_coal);
      }, -Infinity);

      return (item / maxValue) * this.height;
    },
    updatePolyline() {
      this.$nextTick(() => {
        const dots = this.$refs.dots;
        const blocks = this.$refs.blocks;

        if (!dots || !blocks) return;

        const dotsArray = Array.isArray(dots) ? dots : [dots];
        const blocksArray = Array.isArray(blocks) ? blocks : [blocks];

        const svgContainer = blocksArray[0]?.offsetParent;

        const points = dotsArray.map((dot) => {
          const dotEl = dot;
          const parentRect = svgContainer.getBoundingClientRect();
          const dotRect = dotEl.getBoundingClientRect();

          const x = dotRect.left + dotRect.width / 2 - parentRect.left;
          const y = dotRect.top + dotRect.height / 2 - parentRect.top;

          return `${x},${y}`;
        });

        this.polylinePoints = points.join(" ");
      });
    },
  },
};
</script>

<style scoped>
.form-check-input:checked {
  background-color: #dc2626;
  border-color: #dc2626;
}
</style>
